/* eslint-disable hooks/sort */
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';

import api from '@api/api';
import { CommonResponseObj } from '@models/common';
import createVerifier from '@models/dashboardRules';
import {
  IAlarmCountStatistics,
  IEfficiencyTrend,
  ISellerUtilization,
  IStatusRate,
} from '@models/home';
import DashboardDonutChart from '@pages/Home/components/DashboardDonutChart/DashboardDonutChart';
import DashboardPieChart, {
  IData,
} from '@pages/Home/components/DashboardPieChart/DashboardPieChart';
import MetricCard from '@pages/Home/components/MetricCard/MetricCard';
import TreeGraph from '@pages/Home/components/TreeGraph/TreeGraph';
import dataFlowColors from '@pages/Home/models/dataFlowColors';
import { ILink, NODES } from '@pages/Home/models/treeGraph';
import { AppDispatch } from '@store/index';
import { IRootState } from '@store/reducers';
import { setChargingEquipmentStability } from '@store/reducers/dashboardRealTimeValueSlice';
import { formatNumberWithDigits } from '@utils/common';
import { AUTO_UPDATE_INTERVAL } from '@utils/const';
import axios from 'axios';
import dayjs from 'dayjs';

import {
  initSellerUtilization,
  initStatusRate,
  initAlarmStatistics,
  URL,
  serviceStatusRateDataOrder as dataOrder,
  initEfficiencyTrend,
} from '../ChargingEquipmentStability';

import '../ChargingEquipmentStability.scss';

const LINKS: ILink[] = [
  // 充電設備稼動率 #B877f9
  // 充電設備健康(無警示)率 #6FD29F
  // 充電設備充電效率 #2068F6
  // 充電設備服務狀態率 #F7C55D
  {
    data: [NODES.PLATFORM(380, 130), NODES.PILE(550, 300)],
    direction: 'right',
    color: [
      dataFlowColors.ChargingEquipmentUtilization,
      dataFlowColors.ChargingEquipmentHealthRate,
    ],
  },
  {
    data: [NODES.PILE(550, 300), NODES.EV(400, 500)],
    direction: 'left',
    color: [
      dataFlowColors.ChargingEquipmentServiceStatusRate,
      dataFlowColors.ChargingEfficiency,
    ],
  },
];

export default function RealtimeView() {
  const { t } = useTranslation();
  const [sellerUtilization, setSellerUtilization] =
    useState<ISellerUtilization>(initSellerUtilization);
  const [statusRate, setStatusRate] = useState<IStatusRate>(initStatusRate);
  const [alarmStatistics, setAlarmStatistics] =
    useState<IAlarmCountStatistics>(initAlarmStatistics);
  const [sellerEfficiency, setSellerEfficiency] =
    useState<IEfficiencyTrend>(initEfficiencyTrend);

  const commonVerifierRef = useRef(createVerifier());
  const maximumMinuteVerifierRef = useRef(createVerifier());
  const totalChargingGunCountVerifierRef = useRef(createVerifier());
  const pileMaxmunPowerVerifierRef = useRef(createVerifier());
  const sellerId = useSelector((state: IRootState) => state.userSlice.sellerId);
  const basicInfoGunCount = useSelector(
    (state: IRootState) => state.dashboardRealTimeValueSlice.BasicInfo.gunCount,
  );
  const basicInfoMemberCount = useSelector(
    (state: IRootState) => state.dashboardRealTimeValueSlice.BasicInfo.members,
  );
  const chargingPerformanceDuration = useSelector(
    (state: IRootState) =>
      state.dashboardRealTimeValueSlice.FinancialProfitabilityEfficiency
        ?.chargingPerformance?.duration ?? undefined,
  );

  const dispatch = useDispatch<AppDispatch>();

  const timeoutIdRef = useRef<NodeJS.Timeout | number | null>(1);
  const controllersRef = useRef([
    axios.CancelToken.source(),
    axios.CancelToken.source(),
    axios.CancelToken.source(),
    axios.CancelToken.source(),
  ]);

  async function getImmediateData() {
    try {
      const payload = {
        sellerId,
        timeSharing: 'TODAY',
      };

      // 更新 cancel tokens
      const controllers = [
        axios.CancelToken.source(),
        axios.CancelToken.source(),
        axios.CancelToken.source(),
        axios.CancelToken.source(),
      ];
      controllersRef.current = controllers;

      const [
        utilizationRes,
        statusRateRes,
        alarmStatisticsRes,
        sellerEfficiencyRes,
      ] = await Promise.all([
        api.post<CommonResponseObj>(URL.SAAS_UTILIZATION, payload, {
          cancelToken: controllers[0].token,
        }),
        api.post<CommonResponseObj>(URL.SAAS_STATUS_RATE, payload, {
          cancelToken: controllers[1].token,
        }),
        api.post<CommonResponseObj>(URL.ALARM_COUNT_STATISTICS, payload, {
          cancelToken: controllers[2].token,
        }),
        api.post<CommonResponseObj>(URL.SAAS_EFFICIENCY_TREND, payload, {
          cancelToken: controllers[3].token,
        }),
      ]);
      const { data: utilizationResData }: { data: ISellerUtilization } =
        utilizationRes.data;
      setSellerUtilization(utilizationResData);

      const { data: statusRateResData }: { data: IStatusRate } =
        statusRateRes.data;
      setStatusRate(statusRateResData);

      const { data: alarmStatisticsData }: { data: IAlarmCountStatistics } =
        alarmStatisticsRes.data;
      setAlarmStatistics(alarmStatisticsData);

      const { data: sellerEfficiencyData }: { data: IEfficiencyTrend } =
        sellerEfficiencyRes.data;
      console.log('debug sellerEfficiencyData', sellerEfficiencyData);
      setSellerEfficiency(sellerEfficiencyData);
    } catch (error) {
      console.warn('fetch immediate data error: ', error);
    }
  }

  async function recursivelyGetRealtimeData() {
    if (!timeoutIdRef.current) return;

    timeoutIdRef.current = setTimeout(async () => {
      await getImmediateData();
      recursivelyGetRealtimeData();
    }, AUTO_UPDATE_INTERVAL);
  }

  function getRealtimeStatusRateData(): {
    data: IData[];
    percentage: number;
  } {
    if (!statusRate.timeList.length) {
      return {
        data: dataOrder.map((orderItem) => ({
          value: orderItem.key === 'totalOfflineGunCount' ? 1 : 0, // 載入時的暫態 UI
          name: t(orderItem.name),
          itemStyle: {
            color: orderItem.color,
          },
        })),
        percentage: 0,
      };
    }
    const data = dataOrder.map((orderItem) => ({
      value: statusRate.dailyStatusCounts[0][orderItem.key],
      name: t(orderItem.name),
      itemStyle: {
        color: orderItem.color,
      },
    }));

    const percentage = Number(
      formatNumberWithDigits(
        (statusRate.dailyStatusCounts[0].totalChargingGunCount /
          statusRate.dailyStatusCounts[0].totalGunCount) *
          100,
        1,
      ),
    );

    return { data, percentage };
  }

  function getRealtimeAlarmStatisticsData() {
    const initData = [
      {
        value: 0,
        name: t('Model.HighWarning'),
        itemStyle: {
          color: '#FF4D4F',
        },
      },
      {
        value: 0,
        name: t('Model.MediumWarning'),
        itemStyle: {
          color: '#F29500',
        },
      },
      {
        value: 0,
        name: t('Model.LowWarning'),
        itemStyle: {
          color: '#98A2B3',
        },
      },
      {
        value: 1,
        name: t('LongTermView.NoAlert'),
        itemStyle: {
          color: '#E4E7EC',
        },
      },
    ];
    if (!alarmStatistics.timeList.length) {
      return { data: initData, percentage: 0 };
    }

    const data = [
      {
        ...initData[0],
        value: alarmStatistics.highSeverityCountList[0],
      },
      {
        ...initData[1],
        value: alarmStatistics.mediumSeverityCountList[0],
      },
      {
        ...initData[2],
        value: alarmStatistics.lowSeverityCountList[0],
      },
      // 無告警的充電槍
      {
        ...initData[3],
        value:
          alarmStatistics.totalGunCountList[0] -
          alarmStatistics.highSeverityCountList[0] -
          alarmStatistics.mediumSeverityCountList[0] -
          alarmStatistics.lowSeverityCountList[0],
      },
    ];

    const value =
      alarmStatistics.totalGunCountList[0] -
      alarmStatistics.highSeverityCountList[0] -
      alarmStatistics.mediumSeverityCountList[0] -
      alarmStatistics.lowSeverityCountList[0];
    const noAlarmGuns = Number.isNaN(value) ? 0 : value;

    return {
      data,
      percentage: Number(
        formatNumberWithDigits(
          (noAlarmGuns / alarmStatistics.totalGunCountList[0]) * 100,
          1,
        ),
      ),
    };
  }

  const realtimeStatusRateData = getRealtimeStatusRateData();

  const realtimeAlarmStatisticsData = getRealtimeAlarmStatisticsData();

  const noAlarmGuns =
    alarmStatistics.totalGunCountList[0] -
    alarmStatistics.highSeverityCountList[0] -
    alarmStatistics.mediumSeverityCountList[0] -
    alarmStatistics.lowSeverityCountList[0];
  const formattedNoAlarmGuns = Number.isNaN(noAlarmGuns) ? 0 : noAlarmGuns;

  useEffect(() => {
    // fetch function
    getImmediateData();
    recursivelyGetRealtimeData();

    return () => {
      // Clear the timeout when the component unmounts or when activeTab changes
      if (timeoutIdRef.current) {
        if (
          typeof timeoutIdRef.current === 'number' ||
          typeof timeoutIdRef.current === 'object'
        ) {
          clearTimeout(timeoutIdRef.current);
          timeoutIdRef.current = null;
        }
      }
      controllersRef.current.forEach((controller) => {
        controller.cancel('cancel request');
      });
    };
  }, []);

  useEffect(() => {
    const {
      maximumMinutesMap: [maximumMinute],
      normalOperationMinutesMap: [normalOperationMinute],
      utilizationMap: [utilization],
    } = sellerUtilization;

    const dailyStatusCounts = statusRate.dailyStatusCounts[0];

    const {
      totalGunCountList: [totalGunCount],
      highSeverityCountList: [highSeverityCount],
      mediumSeverityCountList: [mediumSeverityCount],
      lowSeverityCountList: [lowSeverityCount],
    } = alarmStatistics;

    const {
      efficiency: [efficiency],
      pileOutputPowers: [pileOutputPower],
      pileMaxmunPowers: [pileMaxmunPower],
    } = sellerEfficiency;

    dispatch(
      setChargingEquipmentStability({
        sellerUtilization: {
          maximumMinute,
          normalOperationMinute,
          utilization,
        },
        statusRate: {
          totalAvailableGunCount: dailyStatusCounts.totalAvailableGunCount,
          totalChargingGunCount: dailyStatusCounts.totalChargingGunCount,
          totalGunCount: dailyStatusCounts.totalGunCount,
          totalOfflineGunCount: dailyStatusCounts.totalOfflineGunCount,
          totalFaultedGunCount: dailyStatusCounts.totalFaultedGunCount,
          totalUnavailableGunCount: dailyStatusCounts.totalUnavailableGunCount,
        },
        alarmCountStatistics: {
          totalGunCount,
          highSeverityCount,
          mediumSeverityCount,
          lowSeverityCount,
        },
        efficiency: {
          efficiency,
          pileOutputPower,
          pileMaxmunPower,
        },
      }),
    );

    // 驗證複數、分子不可大於分母
    commonVerifierRef.current(
      [
        'numerator-cannot-be-greater-than-denominator',
        'numerator-and-denominator-cannot-be-negative',
      ],
      [
        '/api/statistics-service/operationView/getSellerUtilization',
        '/api/statistics-service/operationView/getSellerStatusRate',
        '/api/statistics-service/operationView/getSellerAlarmStatistics',
        '/api/statistics-service/operationView/getSellerEfficiencyTrend',
      ],
      [
        [normalOperationMinute, maximumMinute],
        [
          dailyStatusCounts.totalChargingGunCount,
          dailyStatusCounts.totalGunCount,
        ],
        [
          totalGunCount -
            highSeverityCount -
            mediumSeverityCount -
            lowSeverityCount,
          totalGunCount,
        ],
        [pileOutputPower, pileMaxmunPower],
      ],
      [
        ['normalOperationMinute', 'maximumMinute'],
        ['totalChargingGunCount', 'totalGunCount'],
        [
          'totalGunCount - highSeverityCount - mediumSeverityCount - lowSeverityCount',
          'totalGunCount',
        ],
        ['pileOutputPower', 'pileMaxmunPower'],
      ],
    );

    // 驗證稼動率總時間不可大於現在時間
    if (maximumMinute !== undefined && basicInfoGunCount !== undefined) {
      const startOfToday = dayjs().startOf('day');
      const currentTime = dayjs();
      const currentTimeInMinutes = currentTime.diff(
        startOfToday,
        'minute',
        true,
      );
      const totalMinute = basicInfoGunCount * currentTimeInMinutes;

      maximumMinuteVerifierRef.current(
        ['maximumMinutesMap-cannot-be-greater-than-current-time'],
        [
          [
            '/api/statistics-service/operationView/getSellerUtilization',
            `${t(
              'RealtimeView.ManualCalculationTime',
            )}：${currentTimeInMinutes} * ${t(
              'RealtimeView.GunsCount',
            )}：${basicInfoGunCount}`,
          ],
        ],
        [[maximumMinute, totalMinute]],
        [['maximumMinutes', 'currentTimeInMinutes*GunCount']],
      );
    }

    // 驗證充電中槍總數小於等於會員總數
    if (
      dailyStatusCounts.totalChargingGunCount !== undefined &&
      basicInfoMemberCount !== undefined
    ) {
      totalChargingGunCountVerifierRef.current(
        ['totalChargingGunCount-cannot-be-greater-than-memberCount'],
        [
          [
            '/api/statistics-service/operationView/getSellerStatusRate',
            '/api/member-service/member/getMemberNumNew',
          ],
        ],
        [[dailyStatusCounts.totalChargingGunCount, basicInfoMemberCount]],
        [['totalChargingGunCount', 'members']],
      );
    }

    // 驗證總累計額定能量不能大於槍常見額定功率（180kw）* 總充電時間（分）/ 60
    if (
      pileMaxmunPower !== undefined &&
      chargingPerformanceDuration !== undefined
    ) {
      const RATED_POWER = 180; // 槍常見額定功率常數
      const commonGunPower = (RATED_POWER * chargingPerformanceDuration) / 60; // 槍常見額定功率（180kw）* 總充電時間（分）/ 60
      pileMaxmunPowerVerifierRef.current(
        [
          'pileMaxmunPower-cannot-be-greater-than-commonGunPower-multiplied-by-chargingPerformanceDuration',
        ],
        [
          [
            '/api/statistics-service/operationView/getSellerChargingEfficiency',
            '/api/statistics-service/operationView/getSellerUtilization',
          ],
        ],
        [pileMaxmunPower, commonGunPower],
        ['pileMaxmunPower', 'commonGunPower_multiplied_by_totalChargingTime'],
      );
    }
  }, [sellerUtilization, statusRate, alarmStatistics, sellerEfficiency]);

  // const tempMaxUtilizationMinutes =
  //   (sellerUtilization.maximumMinutesMap[0] ?? 0) *
  //   (statusRate.dailyStatusCounts[0]?.totalGunCount ?? 0);

  return (
    <>
      <TreeGraph connections={LINKS} />

      <MetricCard
        title={t('RealtimeView.EquipmentUtilization')}
        position={{ left: '5%', top: '20%' }}
        ringColor={dataFlowColors.ChargingEquipmentUtilization}
        leftTextArea={{
          subtitle: t('RealtimeView.OperableTimeMins'),
          value: formatNumberWithDigits(
            sellerUtilization.normalOperationMinutesMap[0] ?? 0,
            0,
          ),
          // value: formatNumberWithDigits(tempMaxUtilizationMinutes * 0.072, 0),
        }}
        rightTextArea={{
          subtitle: t('RealtimeView.TotalTimeMins'),
          value: formatNumberWithDigits(
            sellerUtilization.maximumMinutesMap[0] ?? 0,
            0,
          ),
          // value: formatNumberWithDigits(tempMaxUtilizationMinutes, 0),
        }}
        tooltipContent={t('RealtimeView.TotalOperationMinsPeriod')}
      >
        <DashboardDonutChart
          value={
            !sellerUtilization.timeList.length
              ? undefined
              : Number(
                  formatNumberWithDigits(
                    (Math.round(
                      sellerUtilization.normalOperationMinutesMap[0],
                    ) /
                      Math.round(sellerUtilization.maximumMinutesMap[0])) *
                      100 || 0,
                    1,
                  ),
                )
          }
          // value={!sellerUtilization.timeList.length ? undefined : 7.2}
          title={t('RealtimeView.EquipmentUtilization')}
        />
      </MetricCard>
      <MetricCard
        title={t('RealtimeView.ServiceStatusRate')}
        position={{ left: '5%', top: '60%' }}
        ringColor={dataFlowColors.ChargingEquipmentServiceStatusRate}
        leftTextArea={{
          subtitle: t('RealtimeView.TotalChargingGuns'),
          value: formatNumberWithDigits(
            statusRate.dailyStatusCounts[0]?.totalChargingGunCount,
            0,
          ),
        }}
        rightTextArea={{
          subtitle: t('RealtimeView.TotalGuns'),
          value: formatNumberWithDigits(
            statusRate.dailyStatusCounts[0]?.totalGunCount,
            0,
          ),
        }}
        tooltipContent={t('RealtimeView.ProportionOperationalGuns')}
      >
        <DashboardPieChart
          isLoading={!statusRate.timeList.length}
          data={realtimeStatusRateData.data}
          percentage={realtimeStatusRateData.percentage}
        />
      </MetricCard>
      <MetricCard
        title={t('RealtimeView.HealthRate')}
        position={{ left: '55%', top: '20%' }}
        ringColor={dataFlowColors.ChargingEquipmentHealthRate}
        leftTextArea={{
          subtitle: t('RealtimeView.HealthyGuns'),
          value: formatNumberWithDigits(formattedNoAlarmGuns, 0),
        }}
        rightTextArea={{
          subtitle: t('RealtimeView.TotalGuns'),
          value: formatNumberWithDigits(
            alarmStatistics.totalGunCountList[0],
            0,
          ),
        }}
        tooltipContent={t('RealtimeView.ProportionHealthyGuns')}
      >
        <DashboardPieChart
          isLoading={!alarmStatistics.timeList.length}
          data={realtimeAlarmStatisticsData.data}
          percentage={realtimeAlarmStatisticsData.percentage}
          itemFullWidth
        />
      </MetricCard>
      <MetricCard
        title={t('RealtimeView.ChargingEfficiency')}
        position={{ left: '55%', top: '60%' }}
        ringColor={dataFlowColors.ChargingEfficiency}
        leftTextArea={{
          subtitle: t('RealtimeView.TotalOutputEnergy'),
          value: formatNumberWithDigits(
            sellerEfficiency.pileOutputPowers[0],
            0,
          ),
        }}
        rightTextArea={{
          subtitle: t('RealtimeView.TotalRatedEnergy'),
          value: formatNumberWithDigits(
            sellerEfficiency.pileMaxmunPowers[0],
            0,
          ),
        }}
        tooltipContent={t('RealtimeView.ActualOutputRatedEnergy')}
      >
        <DashboardDonutChart
          value={
            !sellerEfficiency.timeList.length
              ? undefined
              : Number(
                  formatNumberWithDigits(
                    (Math.round(sellerEfficiency.pileOutputPowers[0]) /
                      Math.round(sellerEfficiency.pileMaxmunPowers[0])) *
                      100,
                    1,
                  ),
                )
          }
          title={t('RealtimeView.EfficiencyTrend')}
        />
      </MetricCard>
    </>
  );
}
